<template>
  <div class="groups-list">
    <v-banner color="error" dark v-if="fetchAllGroupsError">
      <v-icon slot="icon" color="white" size="36">mdi-wifi-strength-alert-outline</v-icon>
      {{ errorDisplay(fetchAllGroupsError) }}
      <template v-slot:actions>
        <v-btn text color="dark" @click="refresh">Retry</v-btn>
      </template>
    </v-banner>
    <v-banner>
      <v-avatar slot="icon" size="40">
        <v-icon>info</v-icon>
      </v-avatar>
      <div class="title-1">Tips</div>
      <div v-if="displayInfo" class="caption">
        <span>This page is only visible by organization administrators and editors. </span>
        <span>As an organization editor, you can mange which user has access to a specific application through groups. </span>
        <div><span>Groups are a set of application roles containing users.</span></div>
      </div>
      <template v-slot:actions>
        <v-btn text color="dark" @click="displayInfo = !displayInfo">{{displayInfo ?'Hide':'Show'}}</v-btn>
      </template>
    </v-banner>
    <v-container>
      <GroupsTable
        :headers="headers"
        :data="groups ? groups : []"
        :isLoading="fetchAllGroupsLoading"
        v-on:refresh="refresh"
        v-on:editOne="goToGroupDetails"
        v-on:createNew="openGroupCreationDialog"
        v-on:deleteOne="openGroupDeletionDialog"
      />
    </v-container>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-dialog v-model="openOrgaSelector" persistent max-width="600">
      <v-card>
        <v-card-title>Please select your organization</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item v-for="orga in organizations" :key="orga.uuid">
              <v-list-item-icon>
                <v-icon>apartment</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <v-list-item-content>{{orga.name}}</v-list-item-content>
              </v-list-item-title>
              <v-list-item-action>
                <v-btn color="success" @click="selectOrganization(orga)">Select</v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    <GroupCreationDialog
      :dialog="groupCreationDialog"
      :isLoading="isCreationLoading"
      v-on:save="saveGroup"
      v-on:cancel="groupCreationDialog = false;"
      ref="groupCreationDialog"
    />
    <GroupDeletionDialog
      :dialog="groupDeletionDialog"
      :group="groupToDelete"
      :isLoading="isDeletionLoading"
      v-on:cancel="groupDeletionDialog = false; groupToDelete={}"
      v-on:deleteOne="deleteGroup"
    />
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import GroupsTable from "@/components/Access/Groups/GroupsTable";
import GroupCreationDialog from "@/components/Access/Groups/GroupCreationDialog";
import GroupDeletionDialog from "@/components/Access/Groups/GroupDeletionDialog";

export default {
  mixins: [mixin],
  components: {
    GroupsTable,
    GroupCreationDialog,
    GroupDeletionDialog
  },
  data() {
    return {
      snackbar: false,
      openOrgaSelector: false,
      errorMessage: "",
      groupCreationDialog: false,
      groupDeletionDialog: false,
      groupToDelete: {},
      displayInfo: false,
      headers: [
        {
          text: "Name",
          align: "start",
          filterable: true,
          value: "name",
          width: "60%"
        },
        { text: "Actions", value: "actions", sortable: false, align: "right" }
      ]
    };
  },
  computed: {
    ...mapState({
      groups: state => state.organizations.organizationGroups,
      organizations: state => state.organizations.all,
      currentOrganization: state => state.organizations.current,
      fetchAllGroupsLoading: state =>
        state.organizations.fetchAllOrganizationGroups.pending,
      fetchAllGroupsError: state =>
        state.organizations.fetchAllOrganizationGroups.error,
      isCreationLoading: state =>
        state.organizations.createOrganizationGroup.pending,
      isDeletionLoading: state =>
        state.organizations.deleteOrganizationGroup.pending
    })
  },
  watch: {
    currentOrganization() {
      this.refresh();
    }
  },
  methods: {
    ...mapActions("organizations", {
      setCurrentOrganization: "setCurrentOrganization",
      fetchAllOrganizationGroups: "fetchAllOrganizationGroups",
      createOrganizationGroup: "createOrganizationGroup",
      deleteOrganizationGroup: "deleteOrganizationGroup"
    }),
    refresh() {
      this.fetchAllOrganizationGroups({
        organizationUuid: this.currentOrganization.uuid
      }).catch(error => {
        this.snackbar = true;
        this.errorMessage = this.errorDisplay(error);
      });
    },
    selectOrganization(organization) {
      this.setCurrentOrganization({ organization: organization });
      this.openOrgaSelector = false;
    },
    goToGroupDetails(group) {
      this.$router.push(`/access/groups/${group.uuid}`);
    },
    openGroupCreationDialog() {
      this.groupCreationDialog = true;
    },
    saveGroup(groupName) {
      this.createOrganizationGroup({
        organizationUuid: this.currentOrganization.uuid,
        groupName: groupName
      })
        .then(() => {
          this.groupCreationDialog = false;
          this.$refs.groupCreationDialog.reset();
          this.refresh();
        })
        .catch(error => {
          this.errorMessage = this.errorDisplay(error);
          this.snackbar = true;
        });
    },
    openGroupDeletionDialog(group) {
      this.groupToDelete = group;
      this.groupDeletionDialog = true;
    },
    deleteGroup() {
      this.deleteOrganizationGroup({
        organizationUuid: this.currentOrganization.uuid,
        groupUuid: this.groupToDelete.uuid
      })
        .then(() => {
          this.groupDeletionDialog = false;
          this.refresh();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    }
  },
  created() {
    if (this.currentOrganization) {
      this.refresh();
    } else {
      this.openOrgaSelector = true;
    }
  }
};
</script>
